import moment from "moment";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getNumberDisplayValue } from "../../../utils/format";

// ----------------------
// Y‑axis tick generator
// ----------------------
const generateTicks = (min, max, step) => {
  const safeRound = (n) => Number(n.toFixed(4));

  const start = safeRound(Math.round(min / step) * step);
  const end = safeRound(Math.round(max / step) * step);

  const ticks = [];
  let current = start;

  while (current <= end) {
    ticks.push(safeRound(current));
    current += step;
  }

  return ticks;
};

// ----------------------
// Custom X‑axis tick renderer
// ----------------------
const XTick = (props) => {
  const { x, y, payload, ticks, filter, xFormatter } = props;

  // Compute index manually
  const index = ticks.indexOf(payload.value);

  // Hide first tick for 3year / 5year
  if ((filter === "3year" || filter === "5year") && index === 0) {
    return null;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={20}
        textAnchor="end"
        transform="rotate(-45)"
        fontSize={12}
      >
        {xFormatter(payload.value)}
      </text>
    </g>
  );
};

const BrentCrudeLineChart = (props: any) => {
  const Y_STEP = 2;

  const sortedData = [...(props.data || [])].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  // ----------------------
  // Y‑axis domain logic
  // ----------------------
  const values = sortedData.map((d) => d.value) || [];
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;

  const minDomain = Math.floor(min / Y_STEP) * Y_STEP;
  const maxDomain = Math.ceil(max / Y_STEP) * Y_STEP;
  const allTicks = generateTicks(minDomain, maxDomain, Y_STEP);

  const midIndex = Math.floor(allTicks.length / 2);
  const yTicks = [
    allTicks[0],
    allTicks[midIndex],
    allTicks[allTicks.length - 1],
  ];

  // ----------------------
  // X‑axis logic based on filter
  // ----------------------
  const filter = props.filter;

  let xTicks = [];
  let xFormatter = (val) => moment(val).format("DD.MM.YYYY");

  if (filter === "month") {
    xTicks = sortedData.map((d) => d.date);
    xFormatter = (val) => moment(val).format("DD.MM.YYYY");
  } else if (filter === "year") {
    const map = new Map();
    sortedData.forEach((d) => {
      const m = moment(d.date);
      const key = m.format("YYYY-MM");
      if (!map.has(key)) map.set(key, d.date);
    });
    xTicks = Array.from(map.values());
    xFormatter = (val) => moment(val).format("MMM YYYY");
  } else if (filter === "3year" || filter === "5year") {
    const map = new Map();
    sortedData.forEach((d) => {
      const m = moment(d.date);
      const key = m.format("YYYY");
      if (!map.has(key)) map.set(key, d.date);
    });
    xTicks = Array.from(map.values());
    xFormatter = (val) => moment(val).format("YYYY");
  } else {
    xTicks = sortedData.map((d) => d.date);
    xFormatter = (val) => moment(val).format("DD.MM.YYYY");
  }

  return (
    <ResponsiveContainer width="100%" height="100%" minWidth="540px">
      <LineChart
        height={300}
        data={sortedData}
        margin={{
          top: 5,
          right: 15,
          left: 0,
          bottom: 60,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />

        <XAxis
          dataKey="date"
          ticks={xTicks}
          interval={0}
          tick={(props) => (
            <XTick
              {...props}
              ticks={xTicks}
              filter={filter}
              xFormatter={xFormatter}
            />
          )}
        />

        <YAxis
          yAxisId="left"
          domain={[minDomain, maxDomain]}
          ticks={yTicks}
          allowDecimals={true}
          tickFormatter={(val) => val.toString()}
          tick={{ fontSize: 12 }}
        />

        <Tooltip
          labelStyle={{ fontSize: "14px" }}
          contentStyle={{ fontSize: "14px" }}
          formatter={(value, name) => [getNumberDisplayValue(value, 4), name]}
          labelFormatter={(label) => moment(label).format("DD.MM.YYYY")}
        />

        <Line
          yAxisId="left"
          type="monotone"
          dataKey="value"
          stroke="#0d3a88"
          legendType="none"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default BrentCrudeLineChart;
